import React from 'react';

interface WordDisplayProps {
    word: string;
    completedLetters: number;
    currentLetter: number;
    showFullWord?: boolean;
    onLetterRef?: (index: number, element: HTMLDivElement | null) => void;
}

export const WordDisplay: React.FC<WordDisplayProps> = ({ 
    word, 
    completedLetters, 
    currentLetter,
    showFullWord = true,
    onLetterRef
}) => {
    // Always create boxes for all letters to maintain consistent height
    const allLetters = word.split('');
    
    // Dynamic scaling based on word length - better balance for different word sizes
    const getResponsiveSize = (baseSize: number, vwSize: number, maxSize: number) => {
        // Much more generous scaling for short words, aggressive scaling for long words  
        const lengthFactor = allLetters.length <= 3 ? 1.8 : // Very short words get much bigger
                           allLetters.length <= 5 ? 1.4 : // Short words like "כלב" get significantly bigger
                           allLetters.length <= 8 ? Math.max(0.9, 1 - (allLetters.length - 5) * 0.05) :
                           Math.max(0.7, 1 - (allLetters.length - 5) * 0.06); // Long words scaled down more
        return `clamp(${Math.round(baseSize * lengthFactor)}px, ${vwSize * lengthFactor}vw, ${maxSize * lengthFactor}px)`;
    };
    
    return (
    <div style={{
        backgroundColor: 'white',
        padding: 'clamp(16px, 4vw, 32px)',
        borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        marginBottom: 'clamp(12px, 3vw, 24px)',
        width: '100%', // Ensure full width usage
        maxWidth: 'none', // Remove any width restrictions
        overflow: 'visible' // Prevent clipping
    }}>
        <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(18px, 4vw, 24px)',
            fontWeight: 'bold',
            marginBottom: 'clamp(12px, 3vw, 24px)',
            color: '#374151',
            direction: 'rtl'
        }}>
            המילה שלך:
        </h2>
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: getResponsiveSize(2, 0.5, 8),
            direction: 'rtl',
            overflow: 'visible', // Allow animation to be visible
            padding: '8px' // Extra padding to prevent animation clipping
        }}>
            {allLetters.map((letter, index) => {
                // Determine if this letter should be visible based on the mode and progress
                const shouldShowLetter = showFullWord || index < completedLetters;
                
                return (
                    <div
                        key={index}
                        ref={(element) => onLetterRef?.(index, element)}
                        style={{
                            fontSize: getResponsiveSize(28, 6, 72),
                            fontWeight: 'bold',
                            padding: getResponsiveSize(6, 1.5, 16),
                            borderRadius: '8px',
                            border: '4px solid',
                            transition: 'all 0.3s',
                            minWidth: getResponsiveSize(40, 8, 88),
                            minHeight: getResponsiveSize(48, 10, 104),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(index < completedLetters
                                ? { backgroundColor: '#dcfce7', borderColor: '#10b981', color: '#059669' }
                                : index === currentLetter
                                    ? {
                                        backgroundColor: '#fef3c7',
                                        borderColor: '#f59e0b',
                                        color: '#d97706',
                                        animation: 'keyPulse 1.5s ease-in-out infinite',
                                        transformOrigin: 'center bottom'
                                    }
                                    : { backgroundColor: '#f3f4f6', borderColor: '#d1d5db', color: '#9ca3af' }
                            )
                        }}
                    >
                        {shouldShowLetter ? letter : ''}
                    </div>
                );
            })}
        </div>
    </div>
    );
};